import { useState, useEffect, useCallback } from "react";
import {
  Box,
  SimpleGrid,
  Text,
  Button,
  Group,
  Modal,
  Stack,
  ActionIcon,
  Tooltip,
  TextInput,
  Textarea,
  Checkbox,
  Skeleton,
  Center,
} from "@mantine/core";
import {
  IconPhotoPlus,
  IconPhoto,
  IconTrash,
  IconEdit,
  IconDownload,
  IconCamera,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { caseImagesService, CaseImage } from "../../../services";
import ImageUploadModal from "../../../components/cases/ImageUploadModal";
import CameraCaptureModal from "../../../components/cases/CameraCaptureModal";
import { downloadFileFromUrl } from "../../../utils/downloadFile";

interface Props {
  caseId?: string;
}

export default function ImageGalleryV2({ caseId }: Props) {
  const [images, setImages] = useState<CaseImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});

  const [uploadOpen, setUploadOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);

  const [lightboxImage, setLightboxImage] = useState<CaseImage | null>(null);
  const [editImage, setEditImage] = useState<CaseImage | null>(null);
  const [deleteImage, setDeleteImage] = useState<CaseImage | null>(null);

  const [editCaption, setEditCaption] = useState("");
  const [editMagnification, setEditMagnification] = useState("");
  const [editIncludeInReport, setEditIncludeInReport] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchImages = useCallback(async () => {
    if (!caseId) return;
    try {
      setLoading(true);
      const data = await caseImagesService.getImages(caseId);
      setImages(data);
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to load images",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      const urls: Record<string, string> = {};
      await Promise.all(
        images.map(async (img) => {
          const key = img.thumbnailUrl || img.imageUrl;
          if (!key || resolvedUrls[img.id]) return;
          try {
            const { url } = await caseImagesService.getDownloadUrl(
              key,
              img.fileName
            );
            urls[img.id] = url;
          } catch {
            /* skip */
          }
        })
      );
      if (!cancelled) {
        setResolvedUrls((prev) => ({ ...prev, ...urls }));
      }
    };
    if (images.length > 0) resolve();
    return () => {
      cancelled = true;
    };
    // Only re-resolve when image list identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  const resolveFullUrl = useCallback(
    async (img: CaseImage) => {
      try {
        const { url } = await caseImagesService.getDownloadUrl(
          img.imageUrl,
          img.fileName
        );
        return url;
      } catch {
        return resolvedUrls[img.id] ?? "";
      }
    },
    [resolvedUrls]
  );

  const handleOpenLightbox = async (img: CaseImage) => {
    setLightboxImage(img);
    if (!resolvedUrls[`full_${img.id}`]) {
      const url = await resolveFullUrl(img);
      setResolvedUrls((prev) => ({ ...prev, [`full_${img.id}`]: url }));
    }
  };

  const handleOpenEdit = (img: CaseImage) => {
    setEditImage(img);
    setEditCaption(img.caption ?? "");
    setEditMagnification(img.magnification ?? "");
    setEditIncludeInReport(img.includeInReport);
  };

  const handleSaveEdit = async () => {
    if (!editImage || !caseId) return;
    setSaving(true);
    try {
      await caseImagesService.updateImage(caseId, editImage.id, {
        caption: editCaption.trim() || undefined,
        magnification: editMagnification.trim() || undefined,
        includeInReport: editIncludeInReport,
      });
      notifications.show({
        title: "Saved",
        message: "Image details updated",
        color: "green",
      });
      setEditImage(null);
      fetchImages();
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to update image",
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteImage || !caseId) return;
    setDeleting(true);
    try {
      await caseImagesService.deleteImage(caseId, deleteImage.id);
      notifications.show({
        title: "Deleted",
        message: "Image removed",
        color: "green",
      });
      setDeleteImage(null);
      setResolvedUrls((prev) => {
        const next = { ...prev };
        delete next[deleteImage.id];
        delete next[`full_${deleteImage.id}`];
        return next;
      });
      fetchImages();
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to delete image",
        color: "red",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = useCallback(
    async (img: CaseImage) => {
      try {
        if (!img.fileName) throw new Error("The image name is unavailable.");
        const url = await resolveFullUrl(img);
        if (!url) return;
        await downloadFileFromUrl(url, img.fileName);
      } catch {
        notifications.show({
          title: "Error",
          message: "Failed to download image",
          color: "red",
        });
      }
    },
    [resolveFullUrl]
  );

  if (!caseId) return null;

  // --- Loading skeleton ---
  if (loading) {
    return (
      <Box>
        <Text size="lg" fw={700} mb="md">
          Digital Pathology / Images
        </Text>
        <SimpleGrid cols={4} spacing="md">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton
              key={i}
              radius="lg"
              style={{ aspectRatio: "16/9" }}
              h="auto"
            />
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  // --- Empty state ---
  if (images.length === 0) {
    return (
      <Box>
        <Text size="lg" fw={700} mb="md">
          Digital Pathology / Images
        </Text>
        <Center
          style={{
            border: "2px dashed var(--mantine-color-gray-4)",
            borderRadius: 12,
            padding: 48,
          }}
        >
          <Stack align="center" gap="sm">
            <IconPhoto size={48} color="var(--mantine-color-gray-5)" />
            <Text c="dimmed" size="sm">
              No images yet
            </Text>
            <Button
              leftSection={<IconPhotoPlus size={16} />}
              onClick={() => setUploadOpen(true)}
            >
              Add First Image
            </Button>
          </Stack>
        </Center>

        <ImageUploadModal
          opened={uploadOpen}
          onClose={() => setUploadOpen(false)}
          caseId={caseId}
          onUploadSuccess={fetchImages}
        />
        <CameraCaptureModal
          opened={cameraOpen}
          onClose={() => setCameraOpen(false)}
          caseId={caseId}
          onCaptureSuccess={fetchImages}
        />
      </Box>
    );
  }

  // --- Gallery ---
  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Text size="lg" fw={700}>
          Digital Pathology / Images
        </Text>
        <Group gap="xs">
          <Button
            variant="light"
            size="xs"
            leftSection={<IconCamera size={14} />}
            onClick={() => setCameraOpen(true)}
          >
            Capture
          </Button>
          <Button
            size="xs"
            leftSection={<IconPhotoPlus size={14} />}
            onClick={() => setUploadOpen(true)}
          >
            Upload
          </Button>
        </Group>
      </Group>

      <SimpleGrid cols={4} spacing="md">
        {images.map((img) => (
          <Box
            key={img.id}
            style={{
              aspectRatio: "16/9",
              borderRadius: "var(--mantine-radius-lg)",
              overflow: "hidden",
              border: "1px solid var(--mantine-color-gray-3)",
              cursor: "pointer",
              position: "relative",
              backgroundImage: resolvedUrls[img.id]
                ? `url(${resolvedUrls[img.id]})`
                : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundColor: "var(--mantine-color-gray-1)",
            }}
            onClick={() => handleOpenLightbox(img)}
          >
            {/* Bottom gradient overlay */}
            <Box
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
            >
              <Box
                style={{
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.6), transparent)",
                  padding: 8,
                }}
              >
                <Text size="xs" fw={700} c="white" lineClamp={1}>
                  {img.fileName || "Untitled"}
                </Text>
              </Box>
            </Box>

            {/* Hover action icons */}
            <Group
              gap={4}
              style={{
                position: "absolute",
                top: 6,
                right: 6,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Tooltip label="Edit">
                <ActionIcon
                  size="sm"
                  variant="filled"
                  color="dark"
                  style={{ opacity: 0.8 }}
                  onClick={() => handleOpenEdit(img)}
                >
                  <IconEdit size={12} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Download">
                <ActionIcon
                  size="sm"
                  variant="filled"
                  color="dark"
                  style={{ opacity: 0.8 }}
                  onClick={() => handleDownload(img)}
                >
                  <IconDownload size={12} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Delete">
                <ActionIcon
                  size="sm"
                  variant="filled"
                  color="red"
                  style={{ opacity: 0.8 }}
                  onClick={() => setDeleteImage(img)}
                >
                  <IconTrash size={12} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Box>
        ))}

        {/* Add button slot */}
        <Box
          style={{
            aspectRatio: "16/9",
            borderRadius: "var(--mantine-radius-lg)",
            border: "2px dashed var(--mantine-color-gray-4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "border-color 150ms ease",
          }}
          onClick={() => setUploadOpen(true)}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor =
              "var(--mantine-color-blue-5)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor =
              "var(--mantine-color-gray-4)";
          }}
        >
          <IconPhotoPlus size={32} color="var(--mantine-color-gray-5)" />
        </Box>
      </SimpleGrid>

      {/* Lightbox modal */}
      <Modal
        opened={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
        size="xl"
        centered
        title={lightboxImage?.fileName || "Image Preview"}
        padding="md"
      >
        {lightboxImage && (
          <Stack gap="sm">
            <Box
              style={{
                width: "100%",
                maxHeight: "70vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "var(--mantine-color-dark-9)",
                borderRadius: "var(--mantine-radius-md)",
                overflow: "hidden",
              }}
            >
              <img
                src={
                  resolvedUrls[`full_${lightboxImage.id}`] ||
                  resolvedUrls[lightboxImage.id] ||
                  ""
                }
                alt={lightboxImage.caption || ""}
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            </Box>
            <Group justify="space-between">
              <Box>
                {lightboxImage.caption && (
                  <Text size="sm" fw={500}>
                    {lightboxImage.caption}
                  </Text>
                )}
                {lightboxImage.magnification && (
                  <Text size="xs" c="dimmed">
                    Magnification: {lightboxImage.magnification}
                  </Text>
                )}
                {lightboxImage.uploadedBy && (
                  <Text size="xs" c="dimmed">
                    Uploaded by:{" "}
                    {lightboxImage.uploadedBy.fullName ||
                      `${lightboxImage.uploadedBy.firstName ?? ""} ${
                        lightboxImage.uploadedBy.lastName ?? ""
                      }`.trim()}
                  </Text>
                )}
              </Box>
              <Group gap="xs">
                <Tooltip label="Edit">
                  <ActionIcon
                    variant="light"
                    onClick={() => {
                      setLightboxImage(null);
                      handleOpenEdit(lightboxImage);
                    }}
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Download">
                  <ActionIcon
                    variant="light"
                    onClick={() => handleDownload(lightboxImage)}
                  >
                    <IconDownload size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Delete">
                  <ActionIcon
                    variant="light"
                    color="red"
                    onClick={() => {
                      setLightboxImage(null);
                      setDeleteImage(lightboxImage);
                    }}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Edit modal */}
      <Modal
        opened={!!editImage}
        onClose={() => setEditImage(null)}
        title="Edit Image Details"
        centered
      >
        <Stack gap="md">
          <Textarea
            label="Caption"
            placeholder="Describe the image..."
            value={editCaption}
            onChange={(e) => setEditCaption(e.currentTarget.value)}
            minRows={2}
            disabled={saving}
          />
          <TextInput
            label="Magnification"
            placeholder="e.g., 40x, 100x, 200x"
            value={editMagnification}
            onChange={(e) => setEditMagnification(e.currentTarget.value)}
            disabled={saving}
          />
          <Checkbox
            label="Include in final report"
            checked={editIncludeInReport}
            onChange={(e) => setEditIncludeInReport(e.currentTarget.checked)}
            disabled={saving}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={() => setEditImage(null)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} loading={saving}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        opened={!!deleteImage}
        onClose={() => setDeleteImage(null)}
        title="Delete Image"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to delete{" "}
            <Text span fw={600}>
              {deleteImage?.fileName || "this image"}
            </Text>
            ? This action cannot be undone.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={() => setDeleteImage(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Upload & Camera modals */}
      <ImageUploadModal
        opened={uploadOpen}
        onClose={() => setUploadOpen(false)}
        caseId={caseId}
        onUploadSuccess={fetchImages}
      />
      <CameraCaptureModal
        opened={cameraOpen}
        onClose={() => setCameraOpen(false)}
        caseId={caseId}
        onCaptureSuccess={fetchImages}
      />
    </Box>
  );
}
